import type { CountryLanguages } from '~/types/countryLanguages';
import { normalizeCountryEntries, buildCountryLanguagesMap } from '~/utils/countryConfig';
import { StoryblokStaticSlugs, StoryblokProxyEndpoint } from '~/utils/storyblok-constants';
import { COOKIE_NAME_DRAFT_MODE } from '~/types/cookieNames';
import * as Sentry from '@sentry/nuxt';
import type { CountryConfigStoryblok } from '~storyblok-types/CountryConfigStoryblok';
import type { CountryEntryStoryblok } from '~storyblok-types/CountryEntryStoryblok';

type StoryblokStory<T> = {
    full_slug: string;
    content?: T;
    name?: string;
    uuid?: string;
};

/**
 *
 * @param slug
 * @param draftModeCookie    Server-side calls to `getCountryAndLanguage` cause internal
 *                          requests to the Storyblok Proxy. Because of this, Nuxt won't
 *                          automatically include the cookies from the original request.
 *                          To ensure that draft mode works correctly in this scenario,
 *                          we need to manually set the draft mode cookie when making the
 *                          request to the Storyblok Proxy.
 * @returns
 */
const fetchStoriesBySlugs = async <T>(slug: string, draftModeCookie: string | false = false) => {
    return Sentry.startSpan(
        {
            name: 'fetchStoriesBySlugs',
            attributes: {
                draftMode: Boolean(draftModeCookie)
            }
        },
        () => {
            const headers = new Headers();
            if (draftModeCookie) {
                headers.set('Cookie', `${COOKIE_NAME_DRAFT_MODE}=${draftModeCookie}`);
            }

            return $fetch<StoryblokStory<T>[]>(StoryblokProxyEndpoint, {
                query: {
                    type: 'stories',
                    params: {
                        by_slugs: slug,
                        excluding_fields: 'body,header,seo'
                    }
                },
                headers
            });
        }
    );
};

export const getCountryAndLanguage = async (draftModeCookie: string | false = false): Promise<CountryLanguages> => {
    const response = await fetchStoriesBySlugs<CountryConfigStoryblok>(
        StoryblokStaticSlugs.GlobalCountryConfig,
        draftModeCookie
    );

    if (!response[0]) {
        throw new Error(`Global country config not found (tried: "${StoryblokStaticSlugs.GlobalCountryConfig}")`);
    }

    const story = response[0];
    const entries: CountryEntryStoryblok[] = story.content?.CountryList ?? [];

    const normalizedCountries = normalizeCountryEntries(entries);
    const countryLanguages = buildCountryLanguagesMap(normalizedCountries);

    if (Object.keys(countryLanguages).length === 0) {
        throw new Error(`Global country config loaded (${story.full_slug}) but CountryList is empty or invalid`);
    }

    return countryLanguages;
};
